import type { PropsWithChildren } from 'react';
import { forwardRef, useCallback, useImperativeHandle } from 'react';

import classNames from 'classnames';
import { motion, useAnimationControls } from 'framer-motion';
import { useEffectOnce } from 'react-use';

import classes from './HornsAnimation.module.scss';
import type { IHornAnimationVariantsConfig } from './useHornsAnimationVariants';
import { useHornsAnimationVariants } from './useHornsAnimationVariants';

export type HornsAnimationRef = {
  start: () => Promise<void>;
  hide: () => Promise<void>;
  reset: () => void;
};

interface IProps extends Partial<IHornAnimationVariantsConfig> {
  shown?: boolean;
  className?: string;
}

export const HornsAnimation = forwardRef<HornsAnimationRef, PropsWithChildren<IProps>>(function HornsAnimation(
  { shown, className, children, ...config },
  ref,
) {
  const variants = useHornsAnimationVariants(config);
  const containerControls = useAnimationControls();
  const leftControls = useAnimationControls();
  const rightControls = useAnimationControls();

  const playShow = useCallback(async () => {
    await Promise.all([
      containerControls.start('containerShown'),
      leftControls.start('leftShown'),
      rightControls.start('rightShown'),
    ]);
  }, [containerControls, leftControls, rightControls]);

  const playHide = useCallback(async () => {
    await Promise.all([
      containerControls.start('containerHidden'),
      leftControls.start('leftHidden'),
      rightControls.start('rightHidden'),
    ]);
  }, [containerControls, leftControls, rightControls]);

  const doReset = useCallback(() => {
    containerControls.set('containerHidden');
    leftControls.set('leftHidden');
    rightControls.set('rightHidden');
  }, [containerControls, leftControls, rightControls]);

  const setShownImmediately = useCallback(() => {
    containerControls.set('containerShown');
    leftControls.set('leftShown');
    rightControls.set('rightShown');
  }, [containerControls, leftControls, rightControls]);

  useImperativeHandle(ref, () => ({
    start: playShow,
    hide: playHide,
    reset: doReset,
  }));

  useEffectOnce(() => {
    if (shown) {
      setShownImmediately();
    }
  });

  return (
    <div className={classNames(classes.container, className)}>
      <motion.div
        animate={containerControls}
        className={classes.motionsContainer}
        initial="containerHidden"
        variants={variants}
      >
        <motion.div animate={leftControls} className={classes.motionDiv} initial="leftHidden" variants={variants}>
          {children}
        </motion.div>

        <motion.div animate={rightControls} className={classes.motionDiv} initial="rightHidden" variants={variants}>
          {children}
        </motion.div>
      </motion.div>
    </div>
  );
});
